<template>
  <v-dialog
    persistent
    v-model="dialog"
    max-width="500px"
    scrollable
    transition="dialog-transition"
  >
    <template #activator="{ on }">
      <v-btn
        small
        v-on="on"
        outlined
        color="primary"
        class="text-none"
        :disabled="inProgress"
      >
        <v-icon left>mdi-call-split</v-icon>
        {{ $t('downtime.split') }}
      </v-btn>
    </template>
    <v-form ref="form" v-model="isValid" @submit.prevent="splitDowntime">
      <v-card>
        <v-card-title primary-title>
          {{ $t('downtime.splitDowntime') }}
          <v-spacer></v-spacer>
          <v-btn
            icon
            @click="close"
            :disabled="loading"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="4">
              <div class="body-2">
                {{ fieldName === 'linename' ? 'Line' : $t('downtime.machine') }}
              </div>
              <div class="text-uppercase title font-weight-regular mb-2">
                {{ downtime[fieldName] || downtime.machinename }}
              </div>
            </v-col>
            <v-col cols="4">
              <div class="body-2">
                {{ $t('downtime.shift') }}
              </div>
              <div class="text-uppercase title font-weight-regular mb-2">
                {{ downtime.shiftName }}
              </div>
            </v-col>
            <v-col cols="4">
              <div class="body-2">
                {{ $t('downtime.duration') }}
              </div>
              <div class="text-uppercase title font-weight-regular mb-2">
                {{ duration }}
              </div>
            </v-col>
          </v-row>

          <v-row v-for="(dt, index) in downtimes" :key="index">
            <v-col cols="9">
              <v-autocomplete
                dense
                outlined
                return-object
                :label="$t('downtime.reason')"
                :rules="[v => !!v || $t('downtime.validations.reasonRequired')]"
                :items="downtimeReasons"
                item-text="reasonname"
                item-value="reasonname"
                :id="`downtimereason-${index}`"
                v-model="dt.selectedReason"
              >
                <template #selection="data">
                  {{ data.item.reasoncode }} | {{ data.item.reasonname }}
                </template>
                <template #item="data">
                  <v-list-item-content>
                    <v-list-item-title>
                      {{ data.item.reasonname }}
                    </v-list-item-title>
                    <v-list-item-subtitle
                      v-text="data.item.reasoncode"
                    ></v-list-item-subtitle>
                    <v-list-item-subtitle
                      v-text="data.item.category"
                    ></v-list-item-subtitle>
                    <v-list-item-subtitle
                      v-text="data.item.department"
                    ></v-list-item-subtitle>
                  </v-list-item-content>
                </template>
              </v-autocomplete>
              <v-row>
                <v-col cols="6">
                  <v-text-field
                    type="time"
                    outlined
                    dense
                    step="1"
                    :rules="[v => !!v || $t('downtime.validations.startTimeRequired')]"
                    :disabled="index === 0"
                    hide-details="auto"
                    v-model="dt.downtimestart"
                    :id="`downtimestart-${index}`"
                    :label="$t('downtime.startTime')"
                    @input="onStartChange(index)"
                  ></v-text-field>
                </v-col>
                <v-col cols="6">
                  <v-text-field
                    type="time"
                    outlined
                    dense
                    step="1"
                    :rules="[v => !!v || $t('downtime.validations.endTimeRequired')]"
                    :disabled="index === downtimes.length - 1"
                    hide-details="auto"
                    v-model="dt.downtimeend"
                    :id="`downtimeend-${index}`"
                    :label="$t('downtime.endTime')"
                    @input="onEndChange(index)"
                  ></v-text-field>
                </v-col>
              </v-row>
            </v-col>
            <v-col cols="3">
              <v-btn
                icon
                small
                class="mx-2"
                @click="addDowntime(index)"
                :id="`add-${index}`"
                :disabled="loading"
              >
                <v-icon v-text="'$add'"></v-icon>
              </v-btn>
              <v-btn
                icon
                small
                class="pa-0"
                :id="`remove-${index}`"
                @click="removeDowntime(index)"
                :disabled="downtimes.length === 2 || loading"
              >
                <v-icon v-text="'$remove'"></v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            text
            color="primary"
            class="text-none"
            :disabled="loading"
            @click="setDowntimes"
          >
            {{ $t('downtime.reset') }}
          </v-btn>
          <v-btn
            color="primary"
            class="text-none"
            :loading="loading"
            type="submit"
            :disabled="!isValid"
          >
            {{ $t('downtime.split') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
  </v-dialog>
</template>

<script>
import {
  mapState,
  mapActions,
  mapMutations,
  mapGetters,
} from 'vuex';
import { getPlannedValue } from '../utils/downtime-reason';

export default {
  name: 'DowntimeSplit',
  props: {
    downtime: {
      type: Object,
      required: true,
    },
    duration: {
      type: String,
      required: true,
    },
    inProgress: {
      type: Boolean,
      required: true,
    },
    groupRecords: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      isValid: false,
      loading: false,
      dialog: false,
      downtimes: [],
    };
  },
  computed: {
    ...mapState('downtimeLog', ['downtimeReasons']),
    ...mapGetters('downtimeLog', ['machineFilterField']),
    fieldName() {
      return this.machineFilterField;
    },

    sourceRecords() {
      if (this.groupRecords.length) {
        return this.groupRecords;
      }

      if (
        Array.isArray(this.downtime.groupRecords)
        && this.downtime.groupRecords.length
      ) {
        return this.downtime.groupRecords;
      }

      return [this.downtime];
    },

  },
  watch: {
    dialog(value) {
      if (!value) {
        return;
      }

      this.initializeDialog();
    },
  },
  methods: {
    ...mapMutations('helper', ['setAlert']),
    ...mapActions('downtimeLog', ['fetchDowntimeList']),
    ...mapActions('element', ['postRecord', 'updateRecordById']),

    initializeDialog() {
      this.setDowntimes();

      this.resetValidation();
    },

    setDowntimes() {
      const {
        reasonname,
        reasoncode,
        category,
        department,
      } = this.downtime;

      const downtimestart = this.getSplitSourceStart();

      const selectedReason = reasonname
        ? {
          reasonname,
          reasoncode,
          category,
          department,
        }
        : null;

      this.downtimes = [
        {
          downtimestart: this.formatTime(downtimestart),
          downtimeend: '',
          selectedReason,
        },
        {
          downtimestart: '',
          downtimeend: this.formatTime(this.getSplitSourceEnd()),
          selectedReason: null,
        },
      ];

      this.resetValidation();
    },

    resetValidation() {
      this.$nextTick(() => {
        if (this.$refs.form) {
          this.$refs.form.resetValidation();
        }
      });
    },

    onStartChange(index) {
      if (index <= 0) {
        return;
      }

      const previousDowntime = this.downtimes[index - 1];

      this.$set(
        this.downtimes,
        index - 1,
        {
          ...previousDowntime,
          downtimeend: this.downtimes[index].downtimestart,
        },
      );
    },

    onEndChange(index) {
      if (index >= this.downtimes.length - 1) {
        return;
      }

      const nextDowntime = this.downtimes[index + 1];

      this.$set(
        this.downtimes,
        index + 1,
        {
          ...nextDowntime,
          downtimestart: this.downtimes[index].downtimeend,
        },
      );
    },

    addDowntime(index) {
      const lastIndex = this.downtimes.length - 1;
      const lastDowntime = this.downtimes[lastIndex];

      const newDowntime = {
        downtimestart: '',
        downtimeend: lastDowntime.downtimeend,
        selectedReason: null,
      };

      this.$set(
        this.downtimes,
        lastIndex,
        {
          ...lastDowntime,
          downtimeend: '',
        },
      );

      this.downtimes.splice(index + 1, 0, newDowntime);
      this.resetValidation();
    },

    removeDowntime(index) {
      if (this.downtimes.length <= 2) {
        return;
      }

      const removedDowntime = this.downtimes[index];
      const previousDowntime = this.downtimes[index - 1];
      const nextDowntime = this.downtimes[index + 1];

      if (previousDowntime && nextDowntime) {
        this.$set(
          this.downtimes,
          index - 1,
          {
            ...previousDowntime,
            downtimeend: nextDowntime.downtimestart,
          },
        );
      } else if (previousDowntime) {
        this.$set(
          this.downtimes,
          index - 1,
          {
            ...previousDowntime,
            downtimeend: removedDowntime.downtimeend,
          },
        );
      } else if (nextDowntime) {
        this.$set(
          this.downtimes,
          index + 1,
          {
            ...nextDowntime,
            downtimestart: removedDowntime.downtimestart,
          },
        );
      }

      this.downtimes.splice(index, 1);
      this.resetValidation();
    },

    close() {
      if (this.loading) {
        return;
      }

      this.dialog = false;
      this.downtimes = [];
    },

    formatTime(value) {
      const timestamp = Number(value);

      if (Number.isNaN(timestamp)) {
        return '';
      }

      return new Date(timestamp).toLocaleTimeString(
        'en-GB',
        {
          hour12: false,
        },
      );
    },

    getSplitSourceStart() {
      const firstRecord = this.sortedSourceRecords()[0];

      return Number(
        firstRecord && firstRecord.downtimestart
          ? firstRecord.downtimestart
          : this.downtime.downtimestart,
      );
    },

    getSplitSourceEnd() {
      const records = this.sortedSourceRecords();
      const lastRecord = records[
        records.length - 1
      ];

      return Number(
        lastRecord && lastRecord.downtimeend
          ? lastRecord.downtimeend
          : this.downtime.downtimeend,
      );
    },

    sortedSourceRecords() {
      return [...this.sourceRecords].sort((first, second) => (
        Number(first.downtimestart) - Number(second.downtimestart)
      ));
    },

    timeToTimestamp(time, sourceStart) {
      if (!time || !sourceStart) {
        return null;
      }

      const parts = time.split(':');

      if (parts.length < 2) {
        return null;
      }

      const hours = parseInt(parts[0], 10);
      const minutes = parseInt(parts[1], 10);
      const seconds = parseInt(parts[2] || '0', 10);

      if (
        Number.isNaN(hours)
        || Number.isNaN(minutes)
        || Number.isNaN(seconds)
      ) {
        return null;
      }

      const sourceDate = new Date(sourceStart);

      let timestamp = new Date(
        sourceDate.getFullYear(),
        sourceDate.getMonth(),
        sourceDate.getDate(),
        hours,
        minutes,
        seconds,
      ).getTime();

      if (timestamp < sourceStart) {
        timestamp += 86400000;
      }

      return timestamp;
    },

    createSegments() {
      if (!this.downtime) {
        return [];
      }

      const sourceStart = this.getSplitSourceStart();

      const sourceEnd = this.getSplitSourceEnd();
      const lastIndex = this.downtimes.length - 1;

      return this.downtimes.map((downtime, index) => {
        let start = this.timeToTimestamp(
          downtime.downtimestart,
          sourceStart,
        );

        let end = this.timeToTimestamp(
          downtime.downtimeend,
          sourceStart,
        );

        if (index === 0) {
          start = sourceStart;
        }

        if (index === lastIndex) {
          end = sourceEnd;
        }

        if (
          start !== null
          && end !== null
          && end <= start
        ) {
          end += 86400000;
        }

        return {
          ...downtime,
          start,
          end,
        };
      });
    },

    hasInvalidSegments(segments) {
      if (!this.downtime) {
        return true;
      }

      const sourceStart = this.getSplitSourceStart();

      let sourceEnd = this.getSplitSourceEnd();

      if (sourceEnd <= sourceStart) {
        sourceEnd += 86400000;
      }

      return segments.some((segment, index) => {
        const previousSegment = segments[index - 1];

        if (
          segment.start === null
          || segment.end === null
          || segment.end <= segment.start
        ) {
          return true;
        }

        if (
          segment.start < sourceStart
          || segment.end > sourceEnd
        ) {
          return true;
        }

        if (
          previousSegment
          && previousSegment.end !== segment.start
        ) {
          return true;
        }

        return false;
      });
    },

    getReasonFromRecord(record) {
      if (!record || !record.reasonname) {
        return null;
      }

      const {
        reasonname,
        reasoncode,
        category,
        department,
      } = record;

      return {
        reasonname,
        reasoncode,
        category,
        department,
      };
    },

    createDowntimeCopy(sourceRecord = this.downtime) {
      const excludedFields = [
        '_id',
        'elementName',
        'siteId',
        'year',
        'month',
        'week',
        'day',
        'hour',
        'shiftName',
        'isBreak',
        'breakName',
        'isHoliday',
        'holidayName',
        'shiftHour',
        'date',
        'displayHour',
        'hourlyBreakTime',
        'hourlyAvailableTime',
        'shiftBreakTime',
        'shiftAvailableTime',
        'elementId',
        'customerId',
        'userId',
        'createdTimestamp',
        'modifiedtimestamp',
        'frontendId',
        'baseGroupKey',
        'groupKey',
        'groupCount',
        'groupRecords',
        'groupRecordIds',
        'questions',
        'isPlanned',
      ];

      return Object.keys(sourceRecord).reduce(
        (result, key) => {
          if (!excludedFields.includes(key)) {
            result[key] = sourceRecord[key];
          }

          return result;
        },
        {},
      );
    },

    createPayload(segment, downtimeCopy) {
      if (!segment.selectedReason) {
        return null;
      }

      const {
        reasonname,
        reasoncode,
        category,
        department,
      } = segment.selectedReason;

      const durationInMs = segment.end - segment.start;
      const durationInSeconds = Math.floor(durationInMs / 1000);

      const payload = {
        ...downtimeCopy,
        reasonname,
        reasoncode,
        category,
        department,
        downtimestart: segment.start,
        downtimeend: segment.end,
        downtimeinms: durationInMs,
        downtimeduration: durationInSeconds,
        timestamp: segment.start,
      };

      const isPlanned = getPlannedValue(segment.selectedReason, this.downtimeReasons);

      if (isPlanned !== null) {
        payload.isPlanned = isPlanned;
      }

      return payload;
    },

    normalizeRecordRange(record) {
      if (!record) {
        return null;
      }

      const start = Number(record.downtimestart);
      let end = Number(record.downtimeend);

      if (
        Number.isNaN(start)
        || Number.isNaN(end)
      ) {
        return null;
      }

      if (end <= start) {
        end += 86400000;
      }

      return {
        start,
        end,
      };
    },

    findReasonForRange(segments, start, end, fallbackReason) {
      const matchingSegment = segments.find((segment) => (
        start >= segment.start
        && end <= segment.end
      ));

      return matchingSegment && matchingSegment.selectedReason
        ? matchingSegment.selectedReason
        : fallbackReason;
    },

    createPiecesForRange(segments, range, fallbackReason) {
      const boundaries = segments
        .slice(0, -1)
        .map((segment) => segment.end)
        .filter((boundary) => (
          boundary !== null
          && boundary > range.start
          && boundary < range.end
        ))
        .sort((first, second) => first - second);

      const points = [
        range.start,
        ...Array.from(new Set(boundaries)),
        range.end,
      ];

      return points.slice(0, -1).map((start, index) => {
        const end = points[index + 1];

        return {
          start,
          end,
          selectedReason: this.findReasonForRange(
            segments,
            start,
            end,
            fallbackReason,
          ),
        };
      });
    },

    createSplitOperations(segments) {
      const sourceRecords = this.sourceRecords
        .map((record) => ({
          record,
          range: this.normalizeRecordRange(record),
        }))
        .filter(({ record, range }) => record._id && range)
        .sort((
          first,
          second,
        ) => first.range.start - second.range.start);

      return sourceRecords.reduce((operations, { record, range }) => {
        const fallbackReason = this.getReasonFromRecord(record);
        const pieces = this.createPiecesForRange(
          segments,
          range,
          fallbackReason,
        );

        const downtimeCopy = this.createDowntimeCopy(record);
        const updatePayload = this.createPayload(
          pieces[0],
          downtimeCopy,
        );
        const createPayloads = pieces
          .slice(1)
          .map((piece) => this.createPayload(
            piece,
            downtimeCopy,
          ));

        if (
          !updatePayload
          || createPayloads.some((payload) => !payload)
        ) {
          return operations;
        }

        operations.push({
          record,
          updatePayload,
          createPayloads,
        });

        return operations;
      }, []);
    },

    createBoundaryUpdateOperations(segments) {
      return this.sourceRecords
        .map((record) => ({
          record,
          range: this.normalizeRecordRange(record),
        }))
        .filter(({ record, range }) => record._id && range)
        .map(({ record, range }) => {
          const matchingSegment = segments.find((segment) => (
            range.start >= segment.start
            && range.end <= segment.end
          ));

          if (!matchingSegment) {
            return null;
          }

          const downtimeCopy = this.createDowntimeCopy(record);
          const updatePayload = this.createPayload(
            {
              start: range.start,
              end: range.end,
              selectedReason: matchingSegment.selectedReason,
            },
            downtimeCopy,
          );

          if (!updatePayload) {
            return null;
          }

          return {
            record,
            updatePayload,
            createPayloads: [],
          };
        })
        .filter(Boolean);
    },

    isExistingBoundarySplit(segments) {
      const boundaries = segments
        .slice(0, -1)
        .map((segment) => segment.end)
        .filter((boundary) => boundary !== null);

      if (!boundaries.length) {
        return false;
      }

      const recordBoundaries = new Set();

      this.sourceRecords.forEach((record) => {
        const range = this.normalizeRecordRange(record);

        if (!range) {
          return;
        }

        recordBoundaries.add(range.start);
        recordBoundaries.add(range.end);
      });

      return boundaries.every((boundary) => (
        recordBoundaries.has(boundary)
      ));
    },

    saveSplitOperations(operations) {
      return Promise.all(
        operations.map(async ({
          record,
          updatePayload,
          createPayloads,
        }) => {
          const updated = await this.updateRecordById({
            elementName: 'downtime',
            id: record._id,
            payload: updatePayload,
          });

          if (!updated) {
            return false;
          }

          const created = await Promise.all(
            createPayloads.map((payload) => this.postRecord({
              elementName: 'downtime',
              payload,
            })),
          );

          if (!created.every(Boolean)) {
            return false;
          }

          return true;
        }),
      ).then(
        (results) => results.every(Boolean),
      );
    },

    showInvalidSplitAlert() {
      this.setAlert({
        show: true,
        type: 'error',
        message: 'INVALID_DOWNTIME_SPLIT',
      });
    },

    showSplitNotRequiredAlert() {
      this.setAlert({
        show: true,
        type: 'info',
        message: 'DOWNTIME_SPLIT_NOT_REQUIRED',
      });
    },

    async splitDowntime() {
      if (
        !this.$refs.form
        || !this.$refs.form.validate()
      ) {
        return;
      }

      const segments = this.createSegments();

      if (this.hasInvalidSegments(segments)) {
        this.showInvalidSplitAlert();

        return;
      }

      this.loading = true;

      try {
        const operations = this.createSplitOperations(segments);

        if (!operations.length) {
          const existingBoundarySplit = this.isExistingBoundarySplit(
            segments,
          );

          if (existingBoundarySplit) {
            const boundaryOperations = this.createBoundaryUpdateOperations(
              segments,
            );

            if (!boundaryOperations.length) {
              this.showSplitNotRequiredAlert();

              return;
            }

            const saved = await this.saveSplitOperations(
              boundaryOperations,
            );

            if (saved) {
              await this.fetchDowntimeList();
              this.close();
            }
          } else {
            this.showInvalidSplitAlert();
          }

          return;
        }

        const saved = await this.saveSplitOperations(operations);

        if (saved) {
          await this.fetchDowntimeList();
          this.close();
        }
      } catch (error) {
        console.error(
          'Unable to split downtime:',
          error,
        );

        this.setAlert({
          show: true,
          type: 'error',
          message: 'DOWNTIME_SPLIT_FAILED',
        });
      } finally {
        this.loading = false;
      }
    },
    open() {
      this.dialog = true;
    },
  },
};
</script>
